import type { PatternDomain, PatternManifestEntry, PatternSeverity, PatternType } from '~/types';

/**
 * Preferred ordering of known domains in the UI.
 * Domains not in this list still render — they appear after the known ones.
 */
export const DOMAIN_ORDER: PatternDomain[] = [
  'frontend',
  'backend',
  'auth',
  'security',
  'agents',
  'mcp',
  'infrastructure',
  'testing',
  'common',
  'anti-patterns',
];

/**
 * Human-readable labels for known domains.
 * Unknown domains fall back to formatDomainLabel().
 */
const DOMAIN_LABELS: Record<string, string> = {
  frontend: 'Frontend',
  backend: 'Backend',
  auth: 'Auth',
  security: 'Security',
  agents: 'Agents',
  mcp: 'MCP',
  ios: 'iOS',
  infrastructure: 'Infrastructure',
  testing: 'Testing',
  common: 'Common',
  'anti-patterns': 'Anti-Patterns',
};

export interface PatternFilters {
  searchQuery: string;
  selectedDomains: Set<PatternDomain>;
  selectedSeverities: Set<PatternSeverity>;
  selectedTypes?: Set<PatternType>;
  refArchPatternIds?: Set<string> | null;
}

/** Apply search and filter criteria to a list of patterns */
export function filterPatterns(
  patterns: PatternManifestEntry[],
  filters: PatternFilters,
): PatternManifestEntry[] {
  return patterns.filter(p => {
    // Reference architecture filter
    if (filters.refArchPatternIds && !filters.refArchPatternIds.has(p.pattern_id)) {
      return false;
    }
    // Domain filter
    if (filters.selectedDomains.size > 0 && !filters.selectedDomains.has(p.domain)) {
      return false;
    }
    // Severity filter
    if (filters.selectedSeverities.size > 0 && !filters.selectedSeverities.has(p.severity)) {
      return false;
    }
    // Type filter
    if (
      filters.selectedTypes &&
      filters.selectedTypes.size > 0 &&
      !filters.selectedTypes.has(p.type)
    ) {
      return false;
    }
    // Text search: match against pattern_id, intent, and tags
    if (filters.searchQuery) {
      const q = filters.searchQuery.toLowerCase();
      const searchable = [p.pattern_id, p.intent, ...(p.tags ?? [])].join(' ').toLowerCase();
      if (!searchable.includes(q)) {
        return false;
      }
    }
    return true;
  });
}

/** Format a domain slug into a readable label, with lookup for known overrides. */
export function formatDomainLabel(domain: PatternDomain): string {
  if (domain in DOMAIN_LABELS) return DOMAIN_LABELS[domain];
  // Title-case fallback: "gaming" → "Gaming", "anti-patterns" → "Anti-Patterns"
  return domain
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join('-');
}

/**
 * Format a pattern_id into a readable title.
 * "backend-service-decomposition" → "Service Decomposition"
 * "anti-patterns-god-object" → "God Object"
 */
export function formatPatternTitle(patternId: string): string {
  // Strip known domain prefixes
  let stripped = patternId;
  for (const domain of DOMAIN_ORDER) {
    const prefix = `${domain}-`;
    if (stripped.startsWith(prefix)) {
      stripped = stripped.slice(prefix.length);
      break;
    }
  }
  // Title case: split on hyphens, capitalize each word
  return stripped
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Group patterns by domain, respecting DOMAIN_ORDER */
export function groupByDomain(
  patterns: PatternManifestEntry[],
): Map<PatternDomain, PatternManifestEntry[]> {
  const groups = new Map<PatternDomain, PatternManifestEntry[]>();
  for (const pattern of patterns) {
    const existing = groups.get(pattern.domain) || [];
    existing.push(pattern);
    groups.set(pattern.domain, existing);
  }

  // Return in preferred order, then append any domains not in DOMAIN_ORDER
  const ordered = new Map<PatternDomain, PatternManifestEntry[]>();
  for (const domain of DOMAIN_ORDER) {
    const entries = groups.get(domain);
    if (entries && entries.length > 0) {
      ordered.set(domain, entries);
    }
  }
  for (const [domain, entries] of groups.entries()) {
    if (!ordered.has(domain)) {
      ordered.set(domain, entries);
    }
  }
  return ordered;
}

/** Human-readable labels for architecture types */
export const ARCHITECTURE_TYPE_LABELS: Record<string, string> = {
  'api-service': 'API Service',
  'web-application': 'Web Application',
  'ai-agent-system': 'AI Agent System',
  'event-driven': 'Event-Driven',
  serverless: 'Serverless',
  infrastructure: 'Infrastructure',
  'data-platform': 'Data Platform',
  'workflow-automation': 'Workflow Automation',
  'desktop-application': 'Desktop Application',
  'mobile-application': 'Mobile Application',
  monorepo: 'Monorepo',
  'cli-tool': 'CLI Tool',
};

/** Application tier — "I'm building a ___" */
export const APPLICATION_ARCH_TYPES: string[] = [
  'web-application',
  'api-service',
  'mobile-application',
  'desktop-application',
  'cli-tool',
];

/** System tier — "My system uses ___" */
export const SYSTEM_ARCH_TYPES: string[] = [
  'ai-agent-system',
  'event-driven',
  'data-platform',
  'serverless',
  'workflow-automation',
  'infrastructure',
  'monorepo',
];

/** All architecture types in display order */
export const ARCH_TYPE_ORDER: string[] = [...APPLICATION_ARCH_TYPES, ...SYSTEM_ARCH_TYPES];

export interface RefArchTypeGroup {
  type: string;
  label: string;
  entries: Array<import('~/types').RefArchManifestEntry>;
}

/**
 * Format a ref arch variant slug into a readable title.
 * "monolith-api" → "Monolith API"
 */
export function formatRefArchTitle(variant: string): string {
  return variant
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Group ref arch entries by architecture_type, respecting ARCH_TYPE_ORDER */
export function groupRefArchsByType(
  entries: Array<import('~/types').RefArchManifestEntry>,
): RefArchTypeGroup[] {
  const byType = new Map<string, Array<import('~/types').RefArchManifestEntry>>();
  for (const entry of entries) {
    const existing = byType.get(entry.architecture_type) ?? [];
    existing.push(entry);
    byType.set(entry.architecture_type, existing);
  }

  const groups: RefArchTypeGroup[] = [];
  for (const type of ARCH_TYPE_ORDER) {
    const typeEntries = byType.get(type);
    if (typeEntries && typeEntries.length > 0) {
      groups.push({
        type,
        label: ARCHITECTURE_TYPE_LABELS[type] ?? formatRefArchTitle(type),
        entries: typeEntries,
      });
    }
  }
  // Catch any types not in the ordering
  for (const [type, typeEntries] of byType.entries()) {
    if (!ARCH_TYPE_ORDER.includes(type)) {
      groups.push({
        type,
        label: ARCHITECTURE_TYPE_LABELS[type] ?? formatRefArchTitle(type),
        entries: typeEntries,
      });
    }
  }
  return groups;
}

/** Human-readable labels for test layers */
export const TEST_LAYER_LABELS: Record<string, string> = {
  e2e: 'E2E Tests',
  integration: 'Integration Tests',
  unit: 'Unit Tests',
};

/** Display order for test layers */
export const TEST_LAYER_ORDER: string[] = ['e2e', 'integration', 'unit'];

/**
 * Format a framework_id into a readable title.
 * "testing-e2e-framework" → "E2E Framework"
 */
export function formatFrameworkTitle(frameworkId: string): string {
  // Strip "testing-" prefix
  let stripped = frameworkId;
  if (stripped.startsWith('testing-')) {
    stripped = stripped.slice('testing-'.length);
  }
  return stripped
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Strip YAML frontmatter (--- ... ---) from markdown content */
export function stripFrontmatter(content: string): string {
  const match = content.match(/^---\r?\n[\s\S]*?\r?\n---\r?\n?/);
  if (match) {
    return content.slice(match[0].length);
  }
  return content;
}

/**
 * Strip XML-style tags used as agent prompt directives.
 *
 * Framework and pattern files contain tags like `<role>`, `<rules>`, `<context>`
 * that are structural metadata for AI agents. These are not valid HTML and would
 * be silently swallowed by browsers, hiding their content. This removes the
 * wrapper tags while preserving the content between them.
 */
export function stripXmlTags(content: string): string {
  // Match standalone XML open/close tags on their own line (not inside code blocks)
  // e.g. <role>, </role>, <scope_boundaries>, </scope_boundaries>
  return content.replace(/^<\/?[a-z][a-z0-9_-]*>\s*$/gm, '');
}
